import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { translations } from './translations';

const LanguageContext = createContext(null);

const LS_KEY = 'ngrp:preferredLanguage';
const DEFAULT_LANG = 'en';
export const SUPPORTED_LANGS = ['en', 'hi', 'mr'];

function getInitial() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
  } catch (e) {
    void e;
  }
  return DEFAULT_LANG;
}

function resolve(dict, key) {
  if (!key) return '';
  const parts = key.split('.');
  let cur = dict;
  for (const p of parts) {
    if (cur && Object.prototype.hasOwnProperty.call(cur, p)) {
      cur = cur[p];
    } else {
      return null;
    }
  }
  return cur;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitial());

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, lang); } catch (e) { void e; }
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-lang', lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED_LANGS.includes(next)) setLangState(next);
  }, []);

  const t = useCallback((key, fallback) => {
    const primary = resolve(translations[lang] || {}, key);
    if (primary !== null && primary !== undefined) return primary;
    const en = resolve(translations.en || {}, key);
    if (en !== null && en !== undefined) return en;
    return fallback !== undefined ? fallback : key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used inside <LanguageProvider>');
  return ctx;
}
